import React, { useState, useEffect, useCallback } from "react";
import { CreditCard, Check, X, Loader2, Shield, Zap, Crown, ArrowLeft } from "lucide-react";

const API = "/api";

const xhrRequest = (method, url, data) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url, true);
    if (data) xhr.setRequestHeader("Content-Type", "application/json");
    const token = localStorage.getItem("access_token");
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.onload = () => {
      let json = null;
      try { json = JSON.parse(xhr.responseText); } catch {}
      resolve({ status: xhr.status, ok: xhr.status >= 200 && xhr.status < 300, data: json });
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(data ? JSON.stringify(data) : null);
  });
};

export default function SubscriptionPage({ currentUser, onBack, onSubscriptionChange, onLogout }) {
  const [subInfo, setSubInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [pollMessage, setPollMessage] = useState(null);

  const fetchSubInfo = useCallback(async () => {
    try {
      const res = await xhrRequest("GET", `${API}/subscription/info`);
      if (res.ok) setSubInfo(res.data);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchSubInfo();
  }, [fetchSubInfo]);

  // Check for return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const canceled = params.get("canceled");

    if (canceled) {
      setPollMessage({ type: "info", text: "Payment cancelled. You can try again anytime." });
      window.history.replaceState({}, "", window.location.pathname);
      return;
    }

    if (sessionId) {
      setPollMessage({ type: "pending", text: "Verifying your payment..." });
      pollPaymentStatus(sessionId, 0);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempt) => {
    const maxAttempts = 30; // 30 × 2s = 60s total wait
    const interval = 2000;

    if (attempt >= maxAttempts) {
      setPollMessage({ type: "error", text: "Payment verification timed out. Your payment may still be processing — try clicking \"I already paid, sync my access\" below, or refresh this page in a minute." });
      return;
    }

    try {
      const res = await xhrRequest("GET", `${API}/subscription/status/${sessionId}`);
      if (res.ok && (res.data.status === "paid" || res.data.payment_status === "paid" || res.data.complete)) {
        setPollMessage({ type: "success", text: "Payment successful! Your subscription is now active." });
        fetchSubInfo();
        if (onSubscriptionChange) onSubscriptionChange();
        return;
      }
      if (res.ok && res.data.status === "expired") {
        setPollMessage({ type: "error", text: "Payment session expired. Please try again." });
        return;
      }
    } catch {}

    setPollMessage({ type: "pending", text: "Payment is being processed..." });
    setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), interval);
  };

  // Manual reconciliation — hits /subscription/info which self-heals by
  // polling Stripe for any pending transactions and activates if paid.
  const handleSyncAccess = async () => {
    setPollMessage({ type: "pending", text: "Checking with Stripe for your payment..." });
    await fetchSubInfo();
    // fetchSubInfo triggers the self-heal server-side
    try {
      const res = await xhrRequest("GET", `${API}/subscription/info`);
      if (res.ok && res.data.status === "active") {
        setPollMessage({ type: "success", text: "Your payment was found. Subscription is now active." });
        if (onSubscriptionChange) onSubscriptionChange();
      } else {
        setPollMessage({ type: "info", text: "No completed payment found on your account yet. If you just paid, wait 30 seconds and try again. If the issue persists, contact support." });
      }
    } catch {
      setPollMessage({ type: "error", text: "Network error. Please try again." });
    }
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const origin = window.location.origin;
      const res = await xhrRequest("POST", `${API}/subscription/checkout`, { origin_url: origin });
      if (res.ok && res.data.url) {
        window.location.href = res.data.url;
      } else {
        setPollMessage({ type: "error", text: (res.data && res.data.detail) || "Failed to start checkout" });
      }
    } catch {
      setPollMessage({ type: "error", text: "Network error. Please try again." });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You'll lose access to save/export features.")) return;
    setCancelLoading(true);
    try {
      const res = await xhrRequest("POST", `${API}/subscription/cancel`);
      if (res.ok) {
        setPollMessage({ type: "info", text: "Subscription cancelled." });
        fetchSubInfo();
        if (onSubscriptionChange) onSubscriptionChange();
      }
    } catch {} finally { setCancelLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0f]">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  const isActive = subInfo?.status === "active";
  const isTrial = subInfo?.status === "trial";
  const isOwner = subInfo?.is_owner;
  const needsRecurring = subInfo?.status === "needs_recurring";
  const isPastDue = subInfo?.status === "past_due";
  const isExpired = !isActive && !isTrial && !needsRecurring && !isPastDue;
  const cancelAtPeriodEnd = !!subInfo?.cancel_at_period_end;
  const currentPeriodEnd = subInfo?.current_period_end;
  const isLegacyGrace = !!subInfo?.is_legacy_grace;
  const legacyExpiresAt = subInfo?.legacy_expires_at;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      {/* Subtle background */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
        backgroundSize: '32px 32px'
      }} />

      <div className="relative z-10 max-w-2xl mx-auto px-4 py-12">
        {/* Back button (only if onBack is provided — hidden when subscription is required) */}
        {onBack && (
          <button
            data-testid="subscription-back-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Editor
          </button>
        )}

        {!onBack && (
          <div className="mb-8">
            <div data-testid="subscription-required-banner" className="px-4 py-3 rounded-lg text-sm font-medium border bg-rose-500/10 border-rose-500/20 text-rose-400 mb-3">
              {needsRecurring ? (
                <>
                  <strong>Action required:</strong> Your previous plan was a one-time charge that didn't set up monthly billing. Please subscribe below to switch to the <strong>$20/mo recurring plan</strong> — you'll keep the same access, but now with automatic monthly renewals.
                </>
              ) : isPastDue ? (
                <>
                  <strong>Payment failed.</strong> Your latest monthly charge didn't go through. Please subscribe again below to restore access — your card on file will be re-tried automatically each month.
                </>
              ) : (
                <>Your subscription has expired. Please subscribe to continue using BingoGrid AI.</>
              )}
            </div>
            {onLogout && (
              <button onClick={onLogout} className="text-zinc-500 hover:text-white transition-colors text-xs font-medium underline">
                Log out and switch account
              </button>
            )}
          </div>
        )}

        <h1 className="text-3xl font-black tracking-tight mb-2">
          Subscription
        </h1>
        <p className="text-zinc-500 text-sm mb-8">Manage your BingoGrid AI plan</p>

        {/* Status Messages */}
        {pollMessage && (
          <div data-testid="subscription-message" className={`mb-6 px-4 py-3 rounded-lg text-sm font-medium border ${
            pollMessage.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" :
            pollMessage.type === "error" ? "bg-rose-500/10 border-rose-500/20 text-rose-400" :
            pollMessage.type === "pending" ? "bg-amber-500/10 border-amber-500/20 text-amber-400" :
            "bg-blue-500/10 border-blue-500/20 text-blue-400"
          }`}>
            {pollMessage.type === "pending" && <Loader2 className="w-4 h-4 animate-spin inline mr-2" />}
            {pollMessage.text}
          </div>
        )}

        {/* Legacy grace-period banner — the user paid a one-time charge before
            we fixed recurring billing. Their access is still active until
            `legacyExpiresAt`, but they need to subscribe again to lock in
            automatic monthly renewals; otherwise they lose access on that date. */}
        {isLegacyGrace && legacyExpiresAt && (
          <div data-testid="legacy-grace-banner" className="mb-6 px-4 py-3 rounded-lg text-sm border bg-amber-500/10 border-amber-500/20 text-amber-300">
            <div className="font-bold mb-1">Action needed to keep your access</div>
            Your original payment was a one-time charge (not a recurring subscription). We're honoring the rest of your month — your access will remain <strong className="text-amber-200">active until {new Date(legacyExpiresAt).toLocaleDateString()}</strong>. To keep using BingoGrid AI after that, please subscribe below to switch to the <strong>$20/mo recurring plan</strong>. If you don't subscribe by then, access will end until you do.
          </div>
        )}

        {/* Current Status Card */}
        <div className="bg-zinc-900/80 border border-zinc-800 rounded-2xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              {isOwner ? <Crown className="w-5 h-5 text-amber-400" /> :
               isActive ? <Shield className="w-5 h-5 text-emerald-400" /> :
               isTrial ? <Zap className="w-5 h-5 text-amber-400" /> :
               <X className="w-5 h-5 text-rose-400" />}
              <div>
                <h2 className="font-bold text-lg">
                  {isOwner ? "Owner (Unlimited)" :
                   isActive ? "Pro Plan" :
                   isTrial ? "Free Trial" : "No Plan"}
                </h2>
                <p className="text-zinc-500 text-xs">
                  {isOwner ? "Full unlimited access" :
                   isActive ? "$20/month — Full access" :
                   isTrial ? `Trial expires: ${new Date(subInfo?.trial_end).toLocaleDateString()}` :
                   "Upgrade to unlock save & export"}
                </p>
              </div>
            </div>
            <span data-testid="subscription-status-badge" className={`px-3 py-1 rounded-full text-xs font-bold ${
              isOwner || isActive ? "bg-emerald-500/20 text-emerald-400" :
              isTrial ? "bg-amber-500/20 text-amber-400" :
              needsRecurring ? "bg-orange-500/20 text-orange-400" :
              isPastDue ? "bg-rose-500/20 text-rose-400" :
              "bg-rose-500/20 text-rose-400"
            }`}>
              {isOwner ? "OWNER" : isActive ? (cancelAtPeriodEnd ? "CANCELING" : "ACTIVE") :
               isTrial ? "TRIAL" :
               needsRecurring ? "NEEDS RESUBSCRIBE" :
               isPastDue ? "PAST DUE" : "EXPIRED"}
            </span>
          </div>

          {isActive && currentPeriodEnd && (
            <div className="text-xs text-zinc-500 mt-1">
              {cancelAtPeriodEnd
                ? <>Access ends on <strong className="text-zinc-300">{new Date(currentPeriodEnd).toLocaleDateString()}</strong></>
                : <>Next payment on <strong className="text-zinc-300">{new Date(currentPeriodEnd).toLocaleDateString()}</strong></>}
            </div>
          )}

          {/* Features grid */}
          {/* What you get with Pro (there is no free trial — a subscription is
              required for access). */}
          <div className="mt-4">
            <div className="grid grid-cols-2 gap-1 mb-2">
              <span className="text-[10px] text-zinc-500 font-bold">FEATURE</span>
              <span className="text-[10px] text-emerald-400 font-bold text-center">PRO $20/mo</span>
            </div>
            {[
              "Create Templates",
              "AI Image Generation",
              "Board Customization",
              "Markup Tools",
              "Save Templates",
              "Export PNG/PDF",
              "Unlimited Storage",
              "Auto-Markup",
            ].map((name) => {
              const userHas = isOwner || isActive;
              return (
                <div key={name} className="grid grid-cols-2 gap-1 py-1 border-t border-zinc-800/50">
                  <span className={`text-xs ${userHas ? 'text-zinc-300' : 'text-zinc-500'}`}>{name}</span>
                  <div className="flex justify-center">
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action Buttons */}
        {!isOwner && (
          <div className="space-y-3">
            {(!isActive || isLegacyGrace) && (
              <button
                data-testid="subscribe-btn"
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-zinc-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {checkoutLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                {isLegacyGrace ? "Switch to Monthly Auto-Renew — $20/month" : "Subscribe — $20/month"}
              </button>
            )}

            {!isActive && (
              <button
                data-testid="sync-access-btn"
                onClick={handleSyncAccess}
                className="w-full py-3 bg-zinc-900 text-zinc-300 border border-zinc-700 font-medium rounded-xl hover:bg-zinc-800 transition-colors text-sm"
              >
                Already paid? Sync my access
              </button>
            )}

            {isActive && !isLegacyGrace && (
              <button
                data-testid="cancel-subscription-btn"
                onClick={handleCancel}
                disabled={cancelLoading}
                className="w-full py-3 bg-zinc-800 text-zinc-400 font-medium rounded-xl hover:bg-zinc-700 hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
              >
                {cancelLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Cancel Subscription
              </button>
            )}
          </div>
        )}

        <p className="text-center text-zinc-600 text-xs mt-8">
          Payments processed securely via Stripe. Supports cards, Apple Pay, Google Pay, and Link.
        </p>
      </div>
    </div>
  );
}
